import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAssistedAffiliationBanks,
  getAssistedAffiliationBusinessDetails,
  getAssistedAffiliationMunicipalities,
  getAssistedAffiliationNeighborhoods,
  getAssistedAffiliationProvinces,
  getAssistedAffiliationSectors,
} from "@/services/assistedAffiliation/catalogsService";
import type {
  BankCatalogItem,
  BusinessDetailCatalogItem,
  MunicipalityCatalogItem,
  NeighborhoodCatalogItem,
  ProvinceCatalogItem,
  SectorCatalogItem,
} from "@/services/assistedAffiliation/types";
import {
  CATALOG_ERROR_MESSAGE,
  initialCatalogState,
  initialLoadingState,
} from "./constants";
import {
  getCatalogData,
  mapBanksToOptions,
  mapBusinessDetailsToOptions,
  mapMunicipalitiesToOptions,
  mapNeighborhoodsToOptions,
  mapProvincesToOptions,
  mapSectorsToOptions,
} from "./catalogMappers";
import type { CatalogLoadingState, CatalogState } from "./types";

export const useAssistedAffiliationCatalogs = () => {
  const isMountedRef = useRef(true);
  const [catalogs, setCatalogs] = useState<CatalogState>(initialCatalogState);
  const [loading, setLoading] = useState<CatalogLoadingState>(initialLoadingState);
  const [catalogError, setCatalogError] = useState("");

  const updateLoading = useCallback(
    (key: keyof CatalogLoadingState, value: boolean) => {
      if (!isMountedRef.current) return;
      setLoading((current) => ({ ...current, [key]: value }));
    },
    []
  );

  const loadInitialCatalogs = useCallback(async () => {
    updateLoading("initial", true);

    try {
      const [provinceOptions, bankOptions, businessTypeOptions] =
        await Promise.all([
          getAssistedAffiliationProvinces(),
          getAssistedAffiliationBanks(),
          getAssistedAffiliationBusinessDetails(),
        ]);

      if (!isMountedRef.current) return;

      setCatalogs((current) => ({
        ...current,
        bankOptions: mapBanksToOptions(getCatalogData<BankCatalogItem>(bankOptions)),
        businessTypeOptions: mapBusinessDetailsToOptions(
          getCatalogData<BusinessDetailCatalogItem>(businessTypeOptions)
        ),
        provinceOptions: mapProvincesToOptions(
          getCatalogData<ProvinceCatalogItem>(provinceOptions)
        ),
      }));
    } catch (error) {
      if (isMountedRef.current) setCatalogError(CATALOG_ERROR_MESSAGE);
    } finally {
      updateLoading("initial", false);
    }
  }, [updateLoading]);

  const loadMunicipalities = useCallback(
    async (provinceId: string) => {
      setCatalogs((current) => ({
        ...current,
        municipalityOptions: [],
        neighborhoodOptions: [],
        sectorOptions: [],
      }));

      if (!provinceId) return;

      updateLoading("municipalities", true);

      try {
        const municipalityOptions =
          await getAssistedAffiliationMunicipalities(provinceId);
        if (isMountedRef.current) {
          setCatalogs((current) => ({
            ...current,
            municipalityOptions: mapMunicipalitiesToOptions(
              getCatalogData<MunicipalityCatalogItem>(municipalityOptions)
            ),
          }));
        }
      } catch (error) {
        if (isMountedRef.current) setCatalogError(CATALOG_ERROR_MESSAGE);
      } finally {
        updateLoading("municipalities", false);
      }
    },
    [updateLoading]
  );

  const loadSectors = useCallback(
    async (municipalityId: string) => {
      setCatalogs((current) => ({
        ...current,
        neighborhoodOptions: [],
        sectorOptions: [],
      }));

      if (!municipalityId) return;

      updateLoading("sectors", true);

      try {
        const sectorOptions = await getAssistedAffiliationSectors(municipalityId);
        if (isMountedRef.current) {
          setCatalogs((current) => ({
            ...current,
            sectorOptions: mapSectorsToOptions(
              getCatalogData<SectorCatalogItem>(sectorOptions)
            ),
          }));
        }
      } catch (error) {
        if (isMountedRef.current) setCatalogError(CATALOG_ERROR_MESSAGE);
      } finally {
        updateLoading("sectors", false);
      }
    },
    [updateLoading]
  );

  const loadNeighborhoods = useCallback(
    async (sectorId: string) => {
      setCatalogs((current) => ({ ...current, neighborhoodOptions: [] }));

      if (!sectorId) return;

      updateLoading("neighborhoods", true);

      try {
        const neighborhoodOptions =
          await getAssistedAffiliationNeighborhoods(sectorId);
        if (isMountedRef.current) {
          setCatalogs((current) => ({
            ...current,
            neighborhoodOptions: mapNeighborhoodsToOptions(
              getCatalogData<NeighborhoodCatalogItem>(neighborhoodOptions)
            ),
          }));
        }
      } catch (error) {
        if (isMountedRef.current) setCatalogError(CATALOG_ERROR_MESSAGE);
      } finally {
        updateLoading("neighborhoods", false);
      }
    },
    [updateLoading]
  );

  useEffect(() => {
    loadInitialCatalogs();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadInitialCatalogs]);

  return {
    catalogs,
    catalogError,
    clearCatalogError: () => setCatalogError(""),
    loading,
    loadMunicipalities,
    loadNeighborhoods,
    loadSectors,
  };
};
